import { useEffect, useState, useRef } from 'react'
import { Formik, Field, FieldProps } from 'formik'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'
import { Button, Form } from 'react-bootstrap'
import { useLazyQuery } from '@apollo/client'

import { useAppDispatch } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  setAuthentication,
  setIsAuthenticationFlag
} from 'reducers/authentication/authenticationSlice'
import { setCredentials } from 'reducers/authentication/credentialsSlice'
import {
  USER_LOGIN,
  UserLoginResponse,
  UserLoginVariables
} from 'share/queries/login'
import {
  loadTheReduxStoreWithAuthenticationInfoHelper,
  AccountCreationStep
} from 'share/utils/utils'

import './styles.scss'

const LoginSchema = Yup.object().shape({
  userId: Yup.string().required('Email Address (User ID) is required'),
  password: Yup.string().required('Password is required')
})

const LoginForm = (): JSX.Element => {
  const [initialValues, setInitialValues] = useState<UserLoginVariables>({
    userId: '',
    password: '',
    fromAccountCreationFlag: false
  })
  const [userName, setUserName] = useState('')
  const [oldVars, setOldVars] = useState<null | UserLoginVariables>(null)
  const [isLocked, setIsLocked] = useState(false)
  const [emailAddressReset, setEmailAddressReset] = useState(false)
  const [emailAddressResetErrorMessage, setEmailAddressResetErrorMessage] =
    useState('')
  const [mobilePhoneReset, setMobilePhoneReset] = useState(false)
  const [mobilePhoneResetErrorMessage, setMobilePhoneResetErrorMessage] =
    useState('')
  const [doLogin, { loading, data, error }] = useLazyQuery<
    UserLoginResponse,
    UserLoginVariables
  >(USER_LOGIN, { errorPolicy: 'all', fetchPolicy: 'no-cache' })
  const history = useHistory()
  const dispatch = useAppDispatch()
  const formikRef = useRef(null)

  useEffect(() => {
    if (
      error &&
      error.graphQLErrors.length > 0 &&
      error?.graphQLErrors[0].extensions
    ) {
      setIsLocked(error?.graphQLErrors[0].extensions['locked'] == 'true')
      setEmailAddressReset(false)
      setMobilePhoneReset(false)
      setEmailAddressResetErrorMessage('')
      setMobilePhoneResetErrorMessage('')
    }
    if (data && data.authentication.tokenFromUsernameAndPassword && !error) {
      if (
        data.authentication.tokenFromUsernameAndPassword
          .unVerifiedEmailAddressReset ||
        data.authentication.tokenFromUsernameAndPassword
          .unVerifiedMobilePhoneReset
      ) {
        setEmailAddressReset(
          data.authentication.tokenFromUsernameAndPassword
            .unVerifiedEmailAddressReset
        )
        setMobilePhoneReset(
          data.authentication.tokenFromUsernameAndPassword
            .unVerifiedMobilePhoneReset
        )
        if (
          data.authentication.tokenFromUsernameAndPassword
            .unVerifiedEmailAddressReset
        ) {
          const emailErrMsg = `Your unVerified email address ${data.authentication.tokenFromUsernameAndPassword.unVerifiedEmailAddress} was reset to its previous value ${data.authentication.tokenFromUsernameAndPassword.verifiedEmailAddress}. Please retry login with previous value`
          setEmailAddressResetErrorMessage(emailErrMsg)
        }

        if (
          data.authentication.tokenFromUsernameAndPassword
            .unVerifiedMobilePhoneReset
        ) {
          const emailErrMsg = `Your unVerified mobile phone ${data.authentication.tokenFromUsernameAndPassword.unVerifiedMobilePhone} was reset to its previous value ${data.authentication.tokenFromUsernameAndPassword.verifiedMobilePhone}. Please retry login`
          setMobilePhoneResetErrorMessage(emailErrMsg)
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        formikRef.current?.resetForm()
        setInitialValues({
          userId: '',
          password: '',
          fromAccountCreationFlag: false
        })
      } else {
        setEmailAddressReset(false)
        setMobilePhoneReset(false)
        const authentication =
          loadTheReduxStoreWithAuthenticationInfoHelper(data)
        // edge case on requires mfa does not bring back all account data so at least add userName form this page so two factor auth can use it.
        if (
          authentication &&
          authentication.account &&
          !authentication.account.userName
        ) {
          authentication.account.userName = userName
        }
        dispatch(setAuthentication(authentication))
        if (data.authentication.tokenFromUsernameAndPassword.account) {
          if (
            data.authentication.tokenFromUsernameAndPassword.account
              .accountCreationStep
          ) {
            if (
              data.authentication.tokenFromUsernameAndPassword.account
                .accountCreationStep ===
              AccountCreationStep.CreatePasswordStepCompleted
            ) {
              dispatch(
                setCredentials({
                  userName:
                    authentication &&
                    authentication.account &&
                    authentication.account.userName
                      ? authentication.account.userName
                      : null,
                  password: oldVars && oldVars.password,
                  mobilePhone: null
                })
              )
            }
          }
        }

        if (authentication.requiresMfa) {
          history.push({
            pathname: '/login/2fauth',
            state: oldVars
          })
        } else {
          if (
            authentication.account &&
            authentication.account?.accountCreationStep ===
              AccountCreationStep.CompletedAllSteps
          ) {
            dispatch(setIsAuthenticationFlag(true))
            if (
              authentication.account &&
              authentication.account?.caseCount === 1
            ) {
              history.push('/dashboard/case')
            } else {
              history.push('/client')
            }
          }
        }
      }
    }
  }, [data, error])

  const loadingDelay = useLoadingDelay(loading)

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={LoginSchema}
        onSubmit={(values) => {
          setUserName(values.userId)
          if (values == oldVars) return
          setOldVars(values)
          doLogin({
            variables: values
          })
        }}
      >
        {({ errors, touched, handleSubmit }) => (
          <Form onSubmit={handleSubmit} className='ui-login-form'>
            <Field name='userId'>
              {({ field }: FieldProps) => (
                <Form.Group controlId='userId'>
                  <Form.Label>Email Address (User ID)</Form.Label>
                  <Form.Control
                    type='text'
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isInvalid={(!!errors.userId && touched.userId) || !!error}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.userId}
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </Field>

            <Field name='password'>
              {({ field }: FieldProps) => (
                <Form.Group controlId='password'>
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type='password'
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isInvalid={
                      (!!errors.password && touched.password) || !!error
                    }
                  />
                  <Form.Control.Feedback type='invalid'>
                    <span data-testid='login-password-error-text'>
                      {errors.password}
                    </span>
                  </Form.Control.Feedback>
                </Form.Group>
              )}
            </Field>
            <p className='ui-login-form-errors'>
              {error && !isLocked && 'Your user ID or password is incorrect.'}

              <strong className='error-text'>
                {error &&
                error.graphQLErrors.length > 0 &&
                error?.graphQLErrors[0].extensions
                  ? isLocked
                    ? 'You have reached the maximum failed attempts for logging into Client Portal and your account will be locked for 5 minutes. Please refresh the page after 5 minutes and try logging in again. \n\n Please contact technical support at (866) 218-1003 for assistance.'
                    : ` ${error?.graphQLErrors[0].extensions['attemptsremaining']} attempts remaining.`
                  : ''}
              </strong>
              <strong className='error-text'>
                {emailAddressReset && emailAddressResetErrorMessage}
              </strong>
              <strong className='error-text'>
                {mobilePhoneReset && mobilePhoneResetErrorMessage}
              </strong>
            </p>
            <div className='ui-login-submit'>
              <Button
                data-testid='login-button'
                type='submit'
                variant='primary'
                disabled={loading}
              >
                Log In
              </Button>
            </div>

            <div className='ui-login-form-forgot'>
              <a href='/login/forgot' className='ui-login-form-forgot'>
                Forgot <span>login credentials</span>?
              </a>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default LoginForm
